import { buildApiUrl } from './api';

function toText(value, fallback = '') {
  if (typeof value === 'string') {
    return value;
  }

  if (value == null) {
    return fallback;
  }

  return String(value);
}

export function normalizeContentBlock(block) {
  if (!block || typeof block !== 'object') {
    return null;
  }

  const rawId = block.id ?? block.ID ?? block.section ?? block.Section ?? null;

  return {
    ...block,
    id: rawId == null ? null : String(rawId),
    section: toText(block.section ?? block.Section).trim(),
    content: toText(block.content ?? block.Content),
    createdAt: block.created_at ?? block.createdAt ?? block.CreatedAt ?? null,
    updatedAt: block.updated_at ?? block.updatedAt ?? block.UpdatedAt ?? null,
  };
}

export function unwrapContentPayload(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.value)) {
    return payload.value;
  }

  return [];
}

export function normalizeContentBlocks(blocks = []) {
  return blocks.map(normalizeContentBlock).filter(Boolean);
}

export async function fetchContentBlocks() {
  const response = await fetch(buildApiUrl('/content'));

  if (!response.ok) {
    throw new Error('Failed to fetch site content');
  }

  const payload = await response.json().catch(() => []);

  return normalizeContentBlocks(unwrapContentPayload(payload));
}

export function findContentBlock(blocks, ...sections) {
  const normalizedSections = sections
    .flat()
    .map((section) => toText(section).trim().toLowerCase())
    .filter(Boolean);

  return (
    blocks.find((block) => normalizedSections.includes(block.section.trim().toLowerCase())) ?? null
  );
}

export function parseContentValue(content, fallback = null) {
  const rawContent = toText(content).trim();

  if (!rawContent) {
    return fallback;
  }

  try {
    return JSON.parse(rawContent);
  } catch {
    return rawContent;
  }
}

export function getContentValue(blocks, sections, fallback = null) {
  const sectionList = Array.isArray(sections) ? sections : [sections];
  const block = findContentBlock(blocks, ...sectionList);

  return parseContentValue(block?.content, fallback);
}

export function asContentObject(value, fallback = {}) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : fallback;
}

export function asContentArray(value, fallback = []) {
  return Array.isArray(value) ? value : fallback;
}

export function stringifyContentValue(value) {
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

export const CONTENT_TEMPLATES = {
  nav: 'Home, Products, Contact, Blog',
  home: {
    eyebrow: 'Encreage',
    title: 'Write your homepage title here',
    subtitle: 'Write your homepage subtitle here.',
    primaryCtaLabel: 'Contact',
    primaryCtaHref: '/contact',
    secondaryCtaLabel: 'Blog',
    secondaryCtaHref: '/blog',
    sections: [
      {
        title: 'Section title',
        body: 'Section body copy.',
      },
    ],
    closingTitle: 'Optional closing heading',
    closingBody: 'Optional closing body copy.',
  },
  work: {
    eyebrow: 'Selected work',
    title: 'Write your work page title here',
    subtitle: 'Describe the projects or product work you want to highlight.',
    projects: [
      {
        name: 'Project name',
        category: 'Project category',
        status: 'Live',
        description: 'Project summary.',
        stats: [
          { label: 'Metric', value: 'Value' },
        ],
        tags: ['Tag'],
      },
    ],
  },
  contact: {
    eyebrow: 'Contact / new work / introductions',
    title: '',
    subtitle: 'We usually reply within one business day.',
    messagePlaceholder: 'type in your inquiry',
    bestFor: '',
    responseWindow: '',
  },
  store: {
    eyebrow: 'Encreage Store',
    title: 'A minimal store, coming soon.',
    subtitle: 'Use this space to introduce what will be available when the store launches.',
    status: 'Launching soon',
  },
  footer: {
    description: 'Short footer description.',
  },
};
